import {useState, type ReactNode} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {NoAccessState} from '@/components/no-access-state'
import {ExpedienteChrome} from '@/components/expediente-chrome'
import {AuditExportAviso, useAuditExportJob} from '@/components/sgsst/audit-export-status'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useExpedientes} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useIncidentes, INCIDENTES_PAGE_SIZE, type IncidenteListItem} from '@/hooks/sgsst/incidentes/use-incidentes'
import {formatShortDate} from '@/lib/risk'

function IndexRow({to, title}: {to: string; title: string}) {
  return (
    <Link
      to={to}
      data-testid="expediente-index-row"
      className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/40">
      <span className="text-sm font-medium text-foreground">{title}</span>
      <span aria-hidden="true" className="text-primary">
        →
      </span>
    </Link>
  )
}

function ArchiveSection({title, tip, children}: {title: string; tip?: ReactNode; children: ReactNode}) {
  return (
    <section className="surface overflow-hidden">
      <div className="surface-divider space-y-1 border-b px-4 py-3">
        <h2 className="font-heading text-base text-foreground">{title}</h2>
        {tip}
      </div>
      {children}
    </section>
  )
}

function ExportRow({businessId, year}: {businessId: string; year: number}) {
  const {t} = useTranslation()
  const {job, status, building, ready, completable, abriendo, launch, download} = useAuditExportJob(businessId, year)

  return (
    <div className="surface flex flex-col gap-2 px-4 py-4" data-testid="expediente-export">
      <span className="font-heading text-base text-foreground">{t('expediente.exportTitle')}</span>
      <span className="text-xs text-muted-foreground">{t('expediente.exportBody', {year})}</span>
      <div className="mt-1 flex items-center gap-3">
        {ready ? (
          <Button
            size="sm"
            variant="outline"
            disabled={abriendo}
            data-testid="expediente-export-download"
            onClick={download}>
            {t('sgsst.export.download')}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={building}
            data-testid="expediente-export-action"
            onClick={launch}>
            {building ? t('sgsst.export.building') : t('sgsst.export.action')}
          </Button>
        )}
        {completable && (
          <Button size="sm" variant="outline" data-testid="expediente-export-completo" onClick={launch}>
            {t('sgsst.export.completo')}
          </Button>
        )}
        {status === 'error' && <span className="text-xs text-destructive">{t('sgsst.export.error')}</span>}
      </div>
      <AuditExportAviso job={job} />
    </div>
  )
}

function IncidenteRow({businessId, item}: {businessId: string; item: IncidenteListItem}) {
  const {t} = useTranslation()
  return (
    <div
      data-testid="expediente-incidente"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border px-4 py-3 last:border-b-0">
      <span className="text-sm font-medium text-foreground">
        {t(`incidentes.tipo.${item.tipo}`)} · {t(`incidentes.gravedad.${item.gravedad_reportada}`)}
      </span>
      {item.afectado_nombre && <span className="text-xs text-muted-foreground">{item.afectado_nombre}</span>}
      <span className="font-mono text-xs text-muted-foreground">{formatShortDate(item.evento_fecha)}</span>
      <Tag tone="neutral">{t(`incidentes.estado.${item.estado}`)}</Tag>
      {item.investigacion_abierta && (
        <Link
          to={`/businesses/${businessId}/incidentes/${item.id}`}
          data-testid="expediente-incidente-live"
          className="inline-flex items-center gap-1.5">
          <Tag tone="amber">{t('expediente.incidentes.openInvestigation')}</Tag>
          <span className="text-xs text-primary hover:underline">{t('expediente.incidentes.viewLive')}</span>
        </Link>
      )}
    </div>
  )
}

/** El pasado es un lugar (UX O3): cromo propio e índice solo de los artefactos periodizables del año. */
export function ExpedientePage() {
  const {t} = useTranslation()
  const {businessId, year: yearParam} = useParams()
  const year = Number(yearParam)
  const canOperate = useCapabilities(businessId).sgsst_operar
  const {data: expedientes, isLoading} = useExpedientes(canOperate ? businessId : undefined)
  // Paginado: la hoja que se muestra a un inspector no puede callar eventos del año (R6).
  const [incidentesPageNum, setIncidentesPageNum] = useState(1)
  const {data: incidentesPage} = useIncidentes(canOperate ? businessId : undefined, year, incidentesPageNum)
  const incidentes = incidentesPage?.items

  if (!canOperate) return <NoAccessState />
  if (isLoading || !expedientes) return <Skeleton className="h-40 w-full" />

  const exp = expedientes.find((e) => e.period_year === year)
  const currentYear = new Date().getFullYear()

  if (!exp) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('expediente.notFound', {year})}</p>
        <Link to={`/businesses/${businessId}/sgsst`} className="text-sm text-primary hover:underline">
          {t('expediente.back', {year: currentYear})}
        </Link>
      </div>
    )
  }

  const base = `/businesses/${businessId}`
  // Los congelados enlazan a su vista por año (el snapshot del cierre, nunca la versión viva).
  const sections = [
    {key: 'resumen', to: `${base}/sgsst?year=${year}`},
    {key: 'autoevaluacion', to: `${base}/sgsst?year=${year}`},
    {key: 'matrizRiesgos', to: `${base}/expediente/${year}/matriz-riesgos`},
    {key: 'matrizLegal', to: `${base}/expediente/${year}/matriz-legal`},
    {key: 'bateria', to: `${base}/expediente/${year}/bateria`},
    {key: 'planes', to: `${base}/sgsst/plan?year=${year}`},
    {key: 'evidencias', to: `${base}/sgsst/plan?year=${year}`},
  ] as const

  return (
    <ExpedienteChrome exp={exp} backTo={`${base}/sgsst`} backLabel={t('expediente.back', {year: currentYear})}>
      <div className="space-y-4" data-testid="expediente-page">
        <ArchiveSection title={t('expediente.indexTitle')}>
          {sections.map((s) => (
            <IndexRow key={s.key} to={s.to} title={t(`expediente.sections.${s.key}`)} />
          ))}
        </ArchiveSection>

        {/* Por fecha del evento; una investigación abierta al sellar sigue viva en el presente. */}
        <div data-testid="expediente-incidentes">
          <ArchiveSection
            title={t('expediente.sections.incidentes')}
            tip={
              // U10: el expediente se imprime y se muestra a un inspector: la ayuda va en línea.
              <PorQue
                label={t('expediente.incidentes.openInvestigationTipLabel')}
                contenidoTestId="expediente-incidentes-tip"
                siempreVisible>
                {t('expediente.incidentes.openInvestigationHint')}
              </PorQue>
            }>
            {incidentes && incidentes.length > 0 ? (
              <>
                {incidentes.map((item) => (
                  <IncidenteRow key={item.id} businessId={businessId!} item={item} />
                ))}
                <div className="px-4 py-2">
                  <PaginationBar
                    page={incidentesPageNum}
                    pageSize={INCIDENTES_PAGE_SIZE}
                    total={incidentesPage?.total ?? 0}
                    onPageChange={setIncidentesPageNum}
                  />
                </div>
              </>
            ) : (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                {t('expediente.incidentes.empty', {year})}
              </p>
            )}
          </ArchiveSection>
        </div>

        <ExportRow businessId={businessId!} year={year} />
      </div>
    </ExpedienteChrome>
  )
}
